import FileLibraryHelper from "@/helpers/cms/FileLibraryHelper.js";
import {getLanguage, tr} from "@vue-components/helpers/I18nPluginHelper.js";
import MapBaseClass from "./MapBaseClass.js";

export default class MapItem extends MapBaseClass {
    get ignoredFields() {
        return [`map`, `links`, `settings`];
    }

    constructor(data, map) {
        super(data);
        this.map = map;

        this.links = [];

        if (!this.navRule) {
            // Define if the cost of taking this item should be higher
            // recommended | default | not-recommended | avoid | unavailable
            this.navRule = `default`;
        }
        // NavCost is based on distance and navRule
        this.navCost = 0;

        this.offsetAngle = this.image360?.offsetAngle || 0;
        this.image360?.linkedMapItemsOffsets && (this.linkedMapItemsOffsets = this.image360.linkedMapItemsOffsets);
        this.image360 = FileLibraryHelper.getCacheUrl(this.image360);
        this.image = FileLibraryHelper.getCacheUrl(this.image);
        this.building = null;

        // old compatibility
        if (!this.floors) {
            this.floors = {
                list: {},
                multiple: false,
                levelOffset: 0 // when the ground is not flat and elevators have different floors
            };

            if (this.multiFloors) {
                this.floors.multiple = true;
                delete this.multiFloors;

                for (let id of data.floors) {
                    this.floors.list[id] = {
                        accessible: true,
                        name: null
                    };
                }
            }
        }

        this.updateNavCost();
    }

    getName() {
        // One day replace with a CMS shortname
        /*if (this.cmsItem) {
            return tr(this.cmsItem.name);
        } else */
        const label = tr(this.label);
        if (label) {
            return label;
        } else {
            return null;
        }
        /*
        The name can't be translated and should only be used within the map editor
        else if (this.name) {
            return this.name;
        } */
    }

    getSubtitle() {
        const subtitle = tr(this.subtitle);
        if (subtitle) {
            return subtitle;
        } else if (this.address) {
            return this.address;
        } else if (this.cmsItem) {
            return tr(this.cmsItem.getSecondaryValue());
        } else {
            return null;
        }
    }

    getIcon() {
        switch (this.type) {
            case `toilet`:
                if (this.accessible) {
                    return getPublicIcon(`restroom-accessible`, `svg`);
                } else {
                    return getPublicIcon(`restroom`, `svg`);
                }
            case `metro`:
                if (this.stm) {
                    return getPublicIcon(`metro-stm`, `svg`);
                } else {
                    return getPublicIcon(`metro`, `svg`);
                }
            case `bar`:
                return getPublicIcon(`bar`, `svg`);
            case `restaurant`:
                return getPublicIcon(`restaurant`, `svg`);
            case `store`:
                return getPublicIcon(`store`, `svg`);
            case `location`:
                return getPublicIcon(`location`, `svg`);
            case `information`:
                return getPublicIcon(`information`, `svg`);
            case `library`:
                return getPublicIcon(`library`, `svg`);
            case `elevator`:
                return getPublicIcon(`elevator`, `svg`);
            case `stairs`:
                return getPublicIcon(`stairs`, `svg`);
            case `parking`:
                return getPublicIcon(`parking`, `svg`);
            case `parking-machine`:
                return getPublicIcon(`parking-machine`, `svg`);
            case `train`:
                return getPublicIcon(`train`, `svg`);
            case `entrance`:
                return getPublicIcon(`entrance`, `svg`);
            case `parking-barrier`:
                return getPublicIcon(`parking-barrier`, `svg`);
            case `zone-coffee`:
                return getPublicIcon(`zone-coffee`, `svg`);
            case `atm`:
                return getPublicIcon(`atm`, `svg`);
            case `zone-waiting`:
                return getPublicIcon(`zone-waiting`, `svg`);
            case `zone-food`:
                return getPublicIcon(`zone-food`, `svg`);
            case `emergency-exit`:
                return getPublicIcon(`emergency-exit`, `svg`);
            case `water-faucet`:
                return getPublicIcon(`water-faucet`, `svg`);
            case `security`:
                return getPublicIcon(`security`, `svg`);
            case `vending-machine`:
                return getPublicIcon(`vending-machine`, `svg`);
            case `door`:
                return getPublicIcon(`door`, `svg`);
            case `airport`:
                return getPublicIcon(`airport`, `svg`);
            case `baby`:
                return getPublicIcon(`baby`, `svg`);
            case `baby-table`:
                return getPublicIcon(`baby-table`, `svg`);
            case `beach`:
                return getPublicIcon(`beach`, `svg`);
            case `boat`:
                return getPublicIcon(`boat`, `svg`);
            case `church`:
                return getPublicIcon(`church`, `svg`);
            case `dog`:
                return getPublicIcon(`dog`, `svg`);
            case `fountain`:
                return getPublicIcon(`fountain`, `svg`);
            case `lake`:
                return getPublicIcon(`lake`, `svg`);
            case `monument`:
                return getPublicIcon(`monument`, `svg`);
            case `movie`:
                return getPublicIcon(`movie`, `svg`);
            case `museum`:
                return getPublicIcon(`museum`, `svg`);
            case `ocean`:
                return getPublicIcon(`ocean`, `svg`);
            case `school`:
                return getPublicIcon(`school`, `svg`);
            case `statue`:
                return getPublicIcon(`statue`, `svg`);
            case `taxi`:
                return getPublicIcon(`taxi`, `svg`);
            case `zone-parking`:
                return getPublicIcon(`zone-parking`, `svg`);
            case `zone-wheelchairs`:
                return getPublicIcon(`zone-wheelchairs`, `svg`);
            default:
                return getPublicIcon(this.type);

            /* Missing SVG icons for:
                -access-point
                -alarm
                -audio
                -bank
                -barber
                -barrier
                -beacon
                -bed
                -bench
                -broom
                -bus-stop
                -camera
                -car-charger
                -cashier
                -computer
                -cone
                -directions
                -escalator
                -ethernet
                -exchange
                -fiber
                -fire-extinguisher
                -firewall
                -first-aid
                -fitness
                -gasoline
                -hazard
                -hose
                -hospital
                -hydrant
                -kiosk
                -lifebuoy
                -lockers
                -luggage
                -mini-pc
                -modem
                -nav-point
                -node
                -pharmacy
                -player
                -pool
                -post
                -projector
                -qr-code
                -reception
                -recycle-bin
                -rental
                -repeater-hub
                -roulette
                -router
                -scan
                -servers
                -seven
                -shelf
                -slot-machine
                -smoking
                -socket
                -stroller
                -switch
                -table
                -theatre
                -tree
                -tv
                -vault
                -wall
                -wheelchair
                -zone
                -zone-cash
                -zone-employee
                -zone-luggage
                -zone-wheelchairs
            */
        }
    }

    getGlyph(fontAwesomePrefix = `fas`) {
        let glyph = this.glyph;
        if (this.icon?.glyph) {
            glyph = this.icon.glyph;
        }
        else if (this.cmsItem &&
            this.map.integrations.cms.icons?.enabled &&
            this.cmsItem[this.map.integrations.cms.icons.field]) {
            glyph = this.cmsItem[this.map.integrations.cms.icons.field];
        } else {
            glyph = glyph || `fa-location-dot`;
        }
        if (glyph && /^(fak |fab |fas |fal |far ).*/.test(glyph) === false) {
            return `${fontAwesomePrefix} ${glyph}`;
        }
        return glyph;
    }

    getItemColor() {
        if (this.icon?.bg !== null) {
            return this.icon.color;
        }
        return null;
    }

    getFullName() {
        if (this.cmsItem) {
            if (this.cmsItem.hasMultipleMapItems()) {
                // In the case of elevators, use the name on the map to identify uniquely each item
                return this.getName();
            } else {
                return tr(this.cmsItem.getName());
            }
        } else {
            return this.getName();
        }
    }

    getAddress() {
        if (this.address) {
            return this.address;
        } else if (this.cmsItem?.address) {
            return this.cmsItem.address;
        } else {
            return null;
        }
    }

    getLocationDescription() {
        if (tr(this.location)) {
            return tr(this.location);
        } else {
            if (this.cmsItem) {
                return this.getFullName();
            }
            switch (this.type) {
                case `elevator`:
                    return tr(`The Elevators|Les ascenseurs`);
                case `stairs`:
                    return tr(`The Stairs|Les escaliers`);
                case `door`:
                    return tr(`The Door|La porte`);
                case `escalator`:
                    return tr(`The Escalator|L'escalator`);
                case `emergency-exit`:
                    return tr(`The Emergency Exit|La sortie de secours`);
                case `entrance`:
                    if (this.label) {
                        return tr(this.label);
                    } else {
                        return tr(`The Entrance/Exit|L'entrée/sortie`);
                    }
                case `atm`:
                    return tr(`The ATM|Le guichet bancaire`);
            }
        }
    }

    matchPermalink(permalink) {
        if (this.permalink && typeof this.permalink === `string` && permalink === this.permalink) {
            return true;
        } else if (this.permalink && typeof this.permalink === `object` &&
            (permalink === this.permalink.en || permalink === this.permalink.default || permalink === this.permalink.fr)) {
            return true;
        }
        return permalink === this.id;
    }

    getPermalink() {
        if (this.permalink) {
            return tr(this.permalink);
        } else {
            return this.id;
        }
    }

    setCmsItem(cmsItem) {
        this.cmsItem = cmsItem;
        if (this.cmsItem) {
            this.permalink = this.cmsItem.permalink;
        }
    }

    matchSearch(searchRegexp) {
        if (this.cmsItem) {
            return this.cmsItem.matchSearch(searchRegexp);
        } else {
            return searchRegexp.test(this.name);
        }
    }

    updateNavCost() {
        this.navCost = 0;
        if (this.type === `elevator`) {
            this.navCost += 20;
        } else if (this.type === `stairs`) {
            this.navCost += 30;
        }
        switch (this.navRule) {
            case `recommended`:
                this.navCost -= 5;
                break;
            case `not-recommended`:
                this.navCost += 5;
                break;
            case `avoid`:
                this.navCost += 10;
                break;
            case `unavailable`:
                this.navCost += 9999;
                break;
        }
    }

    getNavCost(preferences) {
        if (preferences?.accessible) {
            if (this.type === `stairs` ||
                (this.type === `toilet` && this.accessible === false)) {
                return 9999;
            }
        }
        return this.navCost;
    }

    // Note to prevent access to one specific floor, ensure there is no link
    isOnFloor(floorId) {
        return this.floorId === floorId ||
            (this.floors.multiple && this.floors.list[floorId]?.accessible);
    }

    getFloorWithOffset(floorId) {
        const floor = this.map.getFloorById(floorId);
        if (!floor) {
            return null;
        }
        if (this.floors.levelOffset === 0) {
            return floor;
        }

        return this.map.floors.find(f => f.level === floor.level - this.floors.levelOffset);
    }

    getFloorVariant(floorId) {
        return Object.assign(Object.create(Object.getPrototypeOf(this)), this, { floorId });
    }

    addLink(link) {
        // const existingLink = this.links.find(l => l.id === linkedItem.id);
        this.links.push(link)
    }

    findLink(mapItem) {
        if (!mapItem)
            return null;

        return this.links.find(l => l.isLinkBetween(this, mapItem));
    }

    removeLink(link) {
        this.links.removeItem(link);
    }

    associateBuilding(map) {
        for (const building of map.buildings) {
            if (building.isInside(this)) {
                this.building = building;
                return;
            }
        }
        if (window.env.VUE_APP_ENV !== `live`) {
            console.error(`Missing building for map item ${this.getName()}`);
        }
    }
}
